import { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { GoogleLogin } from '@react-oauth/google';
import { useAuth } from '../../context/AuthContext';
import logoIcon from '../../assets/icons/ep256.png';
import './Home.css';

const Home = () => {
  const navigate = useNavigate();
  const { setAuthState, isAuthenticated } = useAuth();
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigateRef = useRef(false);

  // Token/Secret input state
  const [tokenInput, setTokenInput] = useState('');
  const [showTokenInput, setShowTokenInput] = useState(false);

  // Navigate to dashboard when authenticated (with guard to prevent duplicate navigations)
  useEffect(() => {
    if (isAuthenticated && !navigateRef.current) {
      navigateRef.current = true;
      console.log('Home: Authenticated, navigating to dashboard...');
      navigate('/dashboard', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  const handleGoogleLoginSuccess = async (credentialResponse) => {
    setError('');
    setLoading(true);

    try {
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001/api/v1';

      const response = await fetch(`${apiUrl}/auth/google/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          idToken: credentialResponse.credential,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Login failed');
      }

      // Store tokens and user data
      localStorage.setItem('accessToken', data.data.accessToken);
      localStorage.setItem('refreshToken', data.data.refreshToken);
      localStorage.setItem('user', JSON.stringify(data.data.user));

      // Update AuthContext state - useEffect will handle navigation
      setAuthState(data.data.user);
    } catch (err) {
      setError(err.message || 'Google Sign-In failed');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLoginError = () => {
    setError('Google Sign-In failed. Please try again.');
  };

  const handleTokenSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    if (!tokenInput.trim()) {
      setError('Please enter your token or secret key');
      setLoading(false);
      return;
    }

    try {
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001/api/v1';

      // Check if input is a JWT (3 parts separated by dots)
      const parts = tokenInput.split('.');
      const isJwt = parts.length === 3;

      // Send token/secretKey based on type
      const requestBody = isJwt ? { token: tokenInput } : { apiKey: tokenInput };

      const response = await fetch(`${apiUrl}/auth/dev/token`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestBody),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Invalid token or secret key');
      }

      // Store tokens and user data
      localStorage.setItem('accessToken', data.data.accessToken || tokenInput);
      localStorage.setItem('refreshToken', data.data.refreshToken || '');
      localStorage.setItem('user', JSON.stringify(data.data.user));

      // Update AuthContext state - useEffect will handle navigation
      setAuthState(data.data.user);
    } catch (err) {
      setError(err.message || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="landing-page">
      <div className="landing-card">
        {/* Left Section - Logo and Text */}
        <div className="landing-card-left">
          <div className="landing-logo">
            <img src={logoIcon} alt="EasyPanel Logo" className="logo-image" />
            <div className="brand-container">
              <span className="brand-name">EasyPanel</span>
              <span className="by-inshorts">by Inshorts</span>
            </div>
          </div>
          <h2 className="landing-title">AI-Powered News Summariser</h2>
          <p className="landing-description">
            Create concise news updates with AI. Paste or upload an article to instantly generate a clear 60-word summary for quick reading. Review, refine, and publish accurate, mobile-friendly news from one dashboard.
          </p>
        </div>

        {/* Divider */}
        <div className="landing-divider"></div>

        {/* Right Section - Google Sign-in */}
        <div className="landing-card-right">
          {/* Sign In / Developer Tools Toggle Heading */}
          <button
            type="button"
            className="signin-toggle-heading"
            onClick={() => setShowTokenInput(!showTokenInput)}
          >
            {showTokenInput ? 'Developer Tools' : 'Sign In'}
          </button>

          {error && <div className="auth-error">{error}</div>}

          {/* Content Container - Shows either Google button or Textbox */}
          <div className="signin-content">
            {/* Google Sign-in Section - Only show when Developer Tools is NOT active */}
            {!showTokenInput && (
              <div className="signin-content__google signin-content--visible">
                {loading ? (
                  <div className="auth-loading">Loading...</div>
                ) : (
                  <div className="google-signin-wrapper">
                    <GoogleLogin
                      onSuccess={handleGoogleLoginSuccess}
                      onError={handleGoogleLoginError}
                      theme="outline"
                      size="large"
                      text="signin_with"
                      shape="rectangular"
                      width="280px"
                    />
                  </div>
                )}
              </div>
            )}

            {/* Token/Secret Key Input - Only show when Developer Tools is active */}
            {showTokenInput && (
              <div className="signin-content__dev signin-content--visible">
                <div className="dev-input-section">
                  <form onSubmit={handleTokenSubmit}>
                    <input
                      type="text"
                      className="dev-input"
                      value={tokenInput}
                      onChange={(e) => setTokenInput(e.target.value)}
                      placeholder="Paste your token or secret key..."
                      required
                    />
                    <button
                      type="submit"
                      className="dev-submit-btn"
                      disabled={!tokenInput.trim() || loading}
                    >
                      <svg
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M5 12H19"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M12 5L19 12L12 19"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </button>
                  </form>
                </div>
              </div>
            )}
          </div>
        </div>

        <p className="auth-footer-text">
          By signing in, you agree to our <Link to="#">Terms</Link> and <Link to="#">Privacy Policy</Link>
        </p>
      </div>
    </div>
  );
};

export default Home;
